import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { X, RefreshCw, Clock, CheckCircle2, XCircle, UserPlus, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending: {
    label: 'Pending Approval',
    Icon: Clock,
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    iconClassName: 'text-amber-600',
  },
  approved: {
    label: 'Approved',
    Icon: CheckCircle2,
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconClassName: 'text-emerald-600',
  },
  rejected: {
    label: 'Rejected',
    Icon: XCircle,
    className: 'bg-rose-100 text-rose-800 border-rose-200',
    iconClassName: 'text-rose-600',
  },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatRole = (r) => {
  if (r === 'jr_underwriter') return 'Jr Underwriter';
  return (r || '').charAt(0).toUpperCase() + (r || '').slice(1);
};

/**
 * Modal that lists every user the current user has created, with their current
 * approval status. Used by Jr UWs, Underwriters, and Brokers to follow up on
 * invitations they sent that require admin approval.
 */
const MyCreatedUsersModal = ({ onClose }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/users/created-by-me`);
      setUsers(res.data?.users || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load your created users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const counts = {
    pending: users.filter(u => u.status === 'pending').length,
    approved: users.filter(u => u.status === 'approved').length,
    rejected: users.filter(u => u.status === 'rejected').length,
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="my-created-users-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <div className="flex items-center gap-2 text-slate-800">
              <UserPlus className="h-5 w-5 text-emerald-600" />
              <h3 className="text-lg font-semibold">Users I Created</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Track the approval status of every user you've invited to the portal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchUsers}
              className="text-slate-500 hover:text-slate-700 transition-colors p-1.5 rounded hover:bg-slate-100"
              title="Refresh"
              data-testid="my-created-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="my-created-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Summary chips */}
        {!loading && users.length > 0 && (
          <div className="flex gap-2 flex-wrap px-5 pt-4" data-testid="my-created-summary">
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
              <Clock className="h-3 w-3 mr-1" /> {counts.pending} Pending
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" /> {counts.approved} Approved
            </Badge>
            {counts.rejected > 0 && (
              <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-xs">
                <XCircle className="h-3 w-3 mr-1" /> {counts.rejected} Rejected
              </Badge>
            )}
          </div>
        )}

        {/* Body */}
        <div className="p-5">
          {error && (
            <div className="flex items-start gap-2 text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 mb-3" data-testid="my-created-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <p className="text-center text-sm text-slate-500 py-8" data-testid="my-created-loading">Loading…</p>
          ) : users.length === 0 ? (
            <div className="text-center py-10" data-testid="my-created-empty">
              <UserPlus className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600 font-medium">You haven't created any users yet.</p>
              <p className="text-xs text-slate-500 mt-1">
                When you create a broker or borrower they'll show up here so you can track approval.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {users.map(u => {
                const meta = STATUS_META[u.status] || STATUS_META.pending;
                const { Icon, label, className, iconClassName } = meta;
                return (
                  <div
                    key={u.id}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`my-created-row-${u.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-slate-800 truncate">
                          {u.name || '(no name)'}
                        </h4>
                        <div className="flex gap-1">
                          {(u.roles || []).map(r => (
                            <Badge key={r} variant="outline" className="text-[10px] uppercase tracking-wide">
                              {formatRole(r)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{u.email}</p>
                      {u.company_name && (
                        <p className="text-xs text-slate-400 truncate">{u.company_name}</p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">
                        Created {fmtDate(u.created_at)}
                        {u.approved_at && u.status === 'approved' && (
                          <> · Approved {fmtDate(u.approved_at)}{u.approved_by_name ? ` by ${u.approved_by_name}` : ''}</>
                        )}
                        {u.approved_at && u.status === 'rejected' && (
                          <> · Rejected {fmtDate(u.approved_at)}{u.approved_by_name ? ` by ${u.approved_by_name}` : ''}</>
                        )}
                      </p>
                    </div>
                    <Badge
                      className={`text-[11px] border ${className} whitespace-nowrap flex items-center gap-1 px-2 py-1`}
                      data-testid={`my-created-status-${u.id}`}
                    >
                      <Icon className={`h-3 w-3 ${iconClassName}`} />
                      {label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-5 py-3 border-t bg-slate-50 text-xs text-slate-500">
          <span data-testid="my-created-footer-count">{users.length} total</span>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="my-created-done">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MyCreatedUsersModal;
